import { Box, Button, Divider, HStack, Image, Spinner, Stack, Tag, Text, useDisclosure, VStack } from "@chakra-ui/react";
import { useInfiniteQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { createClickLog, createMedicineEntryLog, getMedicinesByKeywords } from "../../api";
import { colors } from "../../styles";
import { IMedicine, IMedicineListItem, IMedicinesByKeywordsList } from "../../types";
import { formatPrice, noteTagToBackgroundColor, noteTagToLabelColor, noteToTags } from "../../utils";
import MyIcon from "../Icon/MyIcon";
import Alert, { useAlert } from "../Alert/Alert";
import SimilarModal from "../Modals/SimilarModal";
import HMedicineListItem from "./HMedicineListItem";

interface IMedicineGroupListItemProps {
    seqNo:string;
    medicineList:IMedicineListItem[];
    searchKeyword: string;

    isGuest: boolean;
    restrictedAlertMessage?: string;
    isOwnerAndActive: boolean;
}

function MedicineGroupListItem({ seqNo, medicineList, searchKeyword, isGuest, restrictedAlertMessage = "회원가입 후 이용할 수 있습니다.", isOwnerAndActive }:IMedicineGroupListItemProps) {

    ////////////////////////////////////////////////////////////////////// 상태 관리

    const [ represent, setRepresent ] = useState<IMedicine | null>(null);
    const [ imageUrl, setImgUrl ] = useState<string | null>(null); 

    ////////////////////////////////////////////////////////////////////// lastUnitPrices 배열

    const [lastUnitPrices, setLastUnitPrices] = useState<any[]>([]);

    ////////////////////////////////////////////////////////////////////// lastUnitPrices 처리 전 과정

    useEffect(() => {

        ////////////////////////////////////////////////////////////////////// 대표 약품 설정

        if(medicineList[0]) {
            setRepresent(medicineList[0]);

            let urlArray : string[] = [];
            const urlString = medicineList[0].packImage;

            if(urlString) {
                urlArray = urlString.split(';');
                setImgUrl(urlArray[0]);
            }
        }

        ////////////////////////////////////////////////////////////////////// 최대 할인율 추출

        const lastUnitPrices = medicineList.map((medicine) => {
            if (medicine.unitPrice) {

                ////////////////////////////////////////////////////////////////////// lastUnitPrices 배열 처리

                const result = medicine.unitPrice.split(";").map((item, index, array) => {
                    const [count, price] = item.split("|").map(Number);
                    const basePrice = Number(medicine.basePrice);
                    const discount = Math.round(((basePrice - price) / basePrice) * 1000) / 10;
                    return { count, price, discount };
                });
    
                return result[result.length - 1]; // 마지막 값 추출
            }
            return null;
        }).filter(Boolean); // null 값 제거

        ////////////////////////////////////////////////////////////////////// lastUnitPrices 배열 설정

        setLastUnitPrices(lastUnitPrices);

    }, [medicineList]);

    /* 키워드 선택 */

    const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
    const { showAlert, alertProps } = useAlert();

    const changeSelectedKeywords = (keywords: string[]) => {

        if(isGuest) {
            showAlert(restrictedAlertMessage);
            return;
        }

        setSelectedKeywords(keywords);
    }

    /* 키워드를 새로 선택할 때만 기록한다. 해제와 제한 사용자 클릭은 남기지 않는다. */

    const handleKeywordButtonClick = (keywordId: string, keyword: string) => {

        const isSelected = selectedKeywords?.includes(keyword);
        const medicineId = medicineList[0]?.id;

        if (!isSelected && !isGuest && medicineId) {
            createClickLog({
                type: "KEYWORD",
                surface: "SEARCH",
                medicineId,
                keywordId,
                keyword,
            });
        }

        changeSelectedKeywords(isSelected ? selectedKeywords?.filter((selectedKeyword) => selectedKeyword !== keyword) : [...(selectedKeywords || []), keyword]);
    }

    useEffect(() => {
        setSelectedKeywords([]);
    }, [represent?.id]);

    const { isOpen: isOpenSimilarModal, onOpen: onOpenSimilarModal, onClose: onCloseSimilarModal } = useDisclosure();

    /* 동일성분 모달을 여는 클릭만 기록한다 */

    const handleOpenSimilarModal = () => {

        onOpenSimilarModal();

        const medicineId = medicineList[0]?.id;
        if (!medicineId) return;

        createClickLog({
            type: "SIMILAR_MODAL",
            surface: "SEARCH",
            medicineId,
        });
    }

    /* query */

    const size = 16;

    /* useInfiniteQuery */

    const { data: medicinesByKeywordsData, isLoading: isMedicinesByKeywordsDataLoading, fetchNextPage: fetchNextMedicinesByKeywords, hasNextPage: hasNextMedicinesByKeywordsPage, isFetchingNextPage: isFetchingNextMedicinesByKeywordsPage } = useInfiniteQuery<IMedicinesByKeywordsList>({

        // 쿼리 키
        queryKey: ['getMedicinesByKeywords', represent?.id, selectedKeywords],

        // 쿼리 함수 - pageParam은 useInfiniteQuery의 파라미터로 전달되는 값 중 페이지 번호를 의미
        queryFn: ({ pageParam }) => {

            // pageParam은 unknown 타입이므로 명시적으로 number 타입으로 변환
            const page = pageParam as number;
            
            return getMedicinesByKeywords({ 
                medicineId: represent?.id!,
                keywords: selectedKeywords,
                page, 
                size, 
            })
        },

        // 초기 페이지 파라미터 : 1 또는 0 사용
        initialPageParam: 1,

        // 쿼리 활성화 조건
        enabled: !!represent?.id && selectedKeywords.length > 0,

        // 다음 페이지 파라미터 결정
        getNextPageParam: (lastPageData: IMedicinesByKeywordsList) => {
            return lastPageData.hasNextPage ? lastPageData.currentPage + 1 : undefined;
        }
    });

    ////////////////////////////////////////////////////////////////////// 렌더링
    
    return (
        <>
        <Stack
            width={"100%"}
            border={`1px solid ${colors.g5}`}
            borderRadius={"8px"}
            padding={5}
        >
            <Stack
                position="relative"
                alignItems={{
                    base:"center",
                    sm:"center",
                    md:"flex-start",
                    lg:"flex-start",
                    xl:"flex-start",
                }}
                direction={{
                    base:"column",
                    sm:"column",
                    md:"row",
                    lg:"row",
                    xl:"row",
                }}
            >

                {/* 대표 약품 이미지 */}

                {imageUrl && (
                    <Box
                        width="220px"
                        height="220px"
                        minWidth="220px"
                        minHeight="220px"
                        maxWidth="220px"
                        maxHeight="220px"

                        borderRadius="8px"
                        overflow="hidden"
                        marginRight={5}

                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Image
                            src={imageUrl}
                            width="100%"
                            height="100%"
                            objectFit="contain"
                        />
                    </Box>
                )}

                {/* 대표 약품 정보 */}

                <VStack 
                    width={"100%"}
                    justifyContent={"flex-start"}
                    alignItems={"flex-start"}
                    spacing={1}
                >
                    <Text fontSize={14} fontWeight={500} color={colors.g3}>{represent?.seller ?? "-"}</Text>
                    <Text fontSize={18} fontWeight={700} color={colors.g1}>{represent?.nameKor?.split('(')[0].trim() ?? "-"}</Text>
                    <HStack 
                        justifyContent={"flex-start"} 
                        alignItems={"center"} 
                        spacing={1} 
                        marginTop={2}

                        onClick={handleOpenSimilarModal}
                        cursor={"pointer"}
                    >
                        <Text fontSize={14} fontWeight={500} textDecoration={"underline"} color={colors.g1}>{represent?.ingredientInfo ?? "-"}</Text>
                        <MyIcon src={"/svg/window.svg"} alt="external link" width={12} height={12} />
                    </HStack>
                

                    <SimilarModal isOpen={isOpenSimilarModal} onClose={onCloseSimilarModal} medicineId={medicineList[0]?.id ?? ""} surface="SEARCH" />

                    <HStack width={"100%"} justifyContent={"flex-start"} spacing={2} flexWrap={"wrap"} marginTop={4}>
                        {medicineList[0]?.connections?.filter((connection) => connection.weight >= 10).map((connection, index) => (
                            <Button 
                                key={index} 
                                onClick={() => handleKeywordButtonClick(connection.keyword.id, connection.keyword.keyword)}
                                
                                size={"md"} 
                                backgroundColor={selectedKeywords?.includes(connection.keyword.keyword) ? colors.blue : colors.white}
                                borderWidth={2}
                                borderColor={selectedKeywords?.includes(connection.keyword.keyword) ? colors.blue : colors.light}
                                color={selectedKeywords?.includes(connection.keyword.keyword) ? colors.white : colors.black} 
                                _hover={{ 
                                    backgroundColor: selectedKeywords?.includes(connection.keyword.keyword) ? colors.blue : colors.white, 
                                    color: selectedKeywords?.includes(connection.keyword.keyword) ? colors.white : colors.blue 
                                }}
                            >
                                {connection.keyword.keyword}
                            </Button>
                        ))}
                    </HStack>

                    <Divider marginY={3} />

                    {/* 약품 목록 */}

                    <VStack alignItems={"flex-start"}>
                        {medicineList.map((medicine, index) => {

                            const tags: string[] = noteToTags(medicine.note);

                            return (
                                <HStack 
                                    key={index}
                                    justifyContent={"flex-start"} 
                                    width={"100%"}
                                    onClick={() => {
                                        // 검색 목록에서 상세로 바로 여는 클릭
                                        if (medicine.id && searchKeyword) {
                                            createMedicineEntryLog({
                                                source: "SEARCH_LIST",
                                                surface: "SEARCH",
                                                medicineId: medicine.id,
                                                searchKeyword,
                                            });
                                        }
                                        window.open(`/medicine/${medicine.id}`, '_blank', 'noopener,noreferrer');
                                    }}
                                    cursor={"pointer"}
                                >

                                    {/* 패키지 단위 */}

                                    <HStack justifyContent={"flex-start"} alignItems={"center"} spacing={1}>
                                        <MyIcon src="/svg/capsule-filled.svg" alt="medicine" width={16} height={16} />
                                        <Text fontSize={16} fontWeight={700} color={colors.g1}>{medicine.packageUnit}</Text>
                                    </HStack>

                                    {/* 태그 */}
                                    
                                    <HStack 
                                        justifyContent={"flex-start"} 
                                        alignItems={"center"} 
                                        spacing={2}
                                        flexWrap={"wrap"}
                                        marginLeft={2}
                                    >
                                        {tags.map((tag, index) => (
                                            <Tag
                                                key={index}
                                                backgroundColor={noteTagToBackgroundColor(tag)}
                                                color={noteTagToLabelColor(tag)}
                                                size="sm"
                                                borderRadius="full"
                                                padding={2}
                                                fontSize={10}
                                            >
                                                {tag}
                                            </Tag>
                                        ))}
                                    </HStack>
                                </HStack>
                            );
                        })}
                    </VStack>
                </VStack>
            </Stack>

            {/* 키워드에 따른 제품 추천 */}

            {selectedKeywords.length > 0 &&
                <Stack
                    width={"100%"}
                    marginTop={10}
                    // Fade in animation for the whole section
                    animation="fadeInUp 0.6s ease"
                    sx={{
                        '@keyframes fadeInUp': {
                            from: {
                                opacity: 0,
                                transform: 'translate3d(0, 30px, 0)'
                            },
                            to: {
                                opacity: 1,
                                transform: 'none'
                            }
                        }
                    }}
                >
                    <HStack width={"100%"} justifyContent={"flex-start"} spacing={2} flexWrap={"wrap"}>
                        {selectedKeywords.map((keyword, index) => (
                            <Text
                                key={index}
                                fontSize={14}
                                fontWeight={700}
                                color={colors.blue}
                                // 부드럽게 등장
                                animation="fadeKeyword 0.5s"
                                sx={{
                                    animationDelay: `${index * 0.07 + 0.1}s`,
                                    animationFillMode: "backwards",
                                    '@keyframes fadeKeyword': {
                                        from: {
                                            opacity: 0,
                                            transform: 'translateY(10px)'
                                        },
                                        to: {
                                            opacity: 1,
                                            transform: 'none'
                                        }
                                    }
                                }}
                            >{`#${keyword}`}</Text>
                        ))}
                    </HStack>

                    {/* 키워드에 따른 제품 추천 목록 */}

                    <HStack
                        overflowX={"scroll"}
                        width={"100%"}
                        minHeight={"220px"}
                        spacing={4}
                        flexWrap={"nowrap"}
                        paddingY={5}
                    >

                        {isMedicinesByKeywordsDataLoading && (
                            <Spinner color={colors.blue} />
                        )}

                        {(!isMedicinesByKeywordsDataLoading && medicinesByKeywordsData && medicinesByKeywordsData.pages.flatMap((page) => page.medicines).length > 0) && (
                            medicinesByKeywordsData.pages.flatMap((page) => page.medicines).map((medicine, index) => (
                                <HMedicineListItem
                                    key={medicine.id}
                                    index={index}
                                    id={medicine.id ?? ""}
                                    medicineImage={medicine.packImage ?? ""}
                                    seller={medicine.seller ?? ""}
                                    nameKor={medicine.nameKor ?? ""}
                                    packageUnit={medicine.packageUnit ?? ""}
                                    basePrice={medicine.basePrice ?? ""}
                                    unitPrice={medicine.unitPrice ?? ""}
                                    onOpen={() => {
                                        // 검색 카드의 키워드 목록에서 상세로 여는 클릭
                                        const fromMedicineId = medicineList[0]?.id;
                                        if (!medicine.id || !fromMedicineId || selectedKeywords.length === 0) return;

                                        createMedicineEntryLog({
                                            source: "KEYWORD_LIST",
                                            surface: "SEARCH",
                                            medicineId: medicine.id,
                                            fromMedicineId,
                                            searchKeyword,
                                            keywords: selectedKeywords,
                                        });
                                    }}
                                />
                            ))
                        )}

                        {hasNextMedicinesByKeywordsPage && (
                            <Button 
                                variant={"ghost"} 
                                onClick={() => fetchNextMedicinesByKeywords()} 
                                isLoading={isFetchingNextMedicinesByKeywordsPage}
                                
                                size={"xs"}
                                marginLeft={16}
                            >
                                더보기
                            </Button>
                        )}

                        {(!isMedicinesByKeywordsDataLoading && medicinesByKeywordsData && medicinesByKeywordsData.pages.flatMap((page) => page.medicines).length === 0) && (
                            <HStack
                                width={"100%"}
                                minHeight={"220px"}
                                spacing={4}
                                paddingY={5}
                                justifyContent={"center"}
                                alignItems={"center"}
                            >
                                <Text fontSize={14} fontWeight={500} color={colors.g3}>No Medicines</Text>
                            </HStack>
                        )}
                    </HStack>
                    
                </Stack>
            }
        </Stack>
        <Alert {...alertProps} />
        </>
    )
}

export default React.memo(MedicineGroupListItem);